import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { Customer } from '../types';
import Swal from 'sweetalert2';
import { 
  ArrowLeft, 
  Users, 
  Plus, 
  Search, 
  Edit, 
  Eye,
  Trash2, 
  Star,
  Calendar,
  Phone,
  Mail,
  Gift
} from 'lucide-react';

const Container = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
  padding: 24px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 12px;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  color: #6c757d;
  font-size: 14px;
  transition: all 0.2s ease;

  &:hover {
    background: #f8f9fa;
    color: #495057;
  }
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;

  @media (max-width: 768px) {
    width: 100%;
    flex-wrap: wrap;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  max-width: 300px;

  @media (max-width: 768px) {
    max-width: 100%;
    flex: 1 1 100%;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px 12px 44px;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #00d4aa;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const SearchIcon = styled(Search)`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: #00d4aa;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  transition: background-color 0.2s ease;

  &:hover {
    background: #5a67d8;
  }

  @media (max-width: 768px) {
    flex: 1 1 160px;
    justify-content: center;
  }
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 24px;
`;

const StatCard = styled.div`
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  border: 1px solid #e9ecef;
`;

const StatIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: #00d4aa20;
  color: #00d4aa;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
`;

const StatValue = styled.div`
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: #6c757d;
`;

const TableContainer = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  border: 1px solid #e9ecef;
  overflow: hidden;

  @media (max-width: 768px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  @media (max-width: 768px) {
    min-width: 720px;
  }
`;

const TableHeader = styled.thead`
  background: #f8f9fa;
`;

const TableRow = styled.tr`
  &:not(:last-child) {
    border-bottom: 1px solid #e9ecef;
  }

  &:hover {
    background: #f8f9fa;
  }
`;

const TableHeaderCell = styled.th`
  padding: 16px;
  text-align: left;
  font-weight: 600;
  color: #495057;
  font-size: 14px;
  border-bottom: 2px solid #e9ecef;
`;

const TableCell = styled.td`
  padding: 16px;
  color: #495057;
  font-size: 14px;
  vertical-align: middle;
`;

const CustomerInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CustomerAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #00d4aa20;
  color: #00d4aa;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
`;

const CustomerDetails = styled.div``;

const CustomerName = styled.div`
  font-weight: 500;
  color: #333;
`;

const CustomerMeta = styled.div`
  font-size: 12px;
  color: #6c757d;
  margin-top: 2px;
`;

const PointsBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: #28a74520;
  color: #28a745;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button<{ variant?: 'edit' | 'delete' }>`
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  background: ${props => 
    props.variant === 'delete' ? '#dc354520' : '#00d4aa20'
  };
  color: ${props => 
    props.variant === 'delete' ? '#dc3545' : '#00d4aa'
  };

  &:hover {
    background: ${props => 
      props.variant === 'delete' ? '#dc3545' : '#00d4aa'
    };
    color: white;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 48px;
  color: #6c757d;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px;
  color: #6c757d;
`;

const Modal = styled.div<{ $show: boolean }>`
  display: ${props => props.$show ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 32px;
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin: 0 0 24px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #00d4aa;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
`;

const ModalButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 12px 20px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => 
    props.variant === 'primary' ? '#00d4aa' : '#f8f9fa'
  };
  color: ${props => 
    props.variant === 'primary' ? 'white' : '#6c757d'
  };

  &:hover {
    background: ${props => 
      props.variant === 'primary' ? '#5a67d8' : '#e9ecef'
    };
  }
`;

const Tabs = styled.div`
  display: flex;
  gap: 12px;
  margin: 16px 0;
  flex-wrap: wrap;
`;

const TabButton = styled.button<{ $active?: boolean }>`
  padding: 8px 14px;
  border-radius: 999px;
  border: 1px solid ${props => props.$active ? '#00d4aa' : '#e9ecef'};
  background: ${props => props.$active ? '#00d4aa' : 'white'};
  color: ${props => props.$active ? 'white' : '#495057'};
  font-weight: 600;
  cursor: pointer;
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  margin-top: 8px;
`;

const DetailCard = styled.div`
  background: #f8f9fa;
  border-radius: 10px;
  padding: 14px;
  border: 1px solid #e9ecef;
  font-size: 14px;
`;

const HistoryList = styled.div`
  display: grid;
  gap: 12px;
`;

const HistoryItem = styled.div`
  border: 1px solid #e9ecef;
  border-radius: 10px;
  padding: 12px;
  background: #fff;
  display: flex;
  justify-content: space-between;
  gap: 12px;
`;

const HistoryMeta = styled.div`
  font-size: 12px;
  color: #6c757d;
`;

export const CustomersPage: React.FC = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const { user } = useAuth();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [stores, setStores] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [detailsCustomer, setDetailsCustomer] = useState<Customer | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsTab, setDetailsTab] = useState<'overview' | 'points' | 'redemptions'>('overview');
  const [pointsHistory, setPointsHistory] = useState<any[]>([]);
  const [redemptionsHistory, setRedemptionsHistory] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [formData, setFormData] = useState<{
    name: string;
    cpf: string;
    email: string;
    phone: string;
    birth_date: string;
    gender: '' | 'male' | 'female' | 'other';
    store_id: string;
  }>({
    name: '',
    cpf: '',
    email: '',
    phone: '',
    birth_date: '',
    gender: '',
    store_id: ''
  });

  const loadStores = useCallback(async () => {
    try {
      const response = await apiService.getStores();
      if (response.success) {
        setStores(response.data || []);
      }
    } catch (error) {
      console.error('Erro ao carregar lojas:', error);
    }
  }, []);

  const loadCustomers = useCallback(async () => {
    try {
      const response = await apiService.getCustomers();
      if (response.success) {
        setCustomers(response.data || []);
      } else {
        throw new Error(response.message || 'Erro ao carregar clientes');
      }
    } catch (error: any) {
      console.error('Erro ao carregar clientes:', error);
      showError('Erro ao carregar clientes. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    loadCustomers();
    loadStores();
  }, [loadCustomers, loadStores]);

  const handleAddCustomer = () => {
    setEditingCustomer(null);
    setFormData({
      name: '',
      cpf: '',
      email: '',
      phone: '',
      birth_date: '',
      gender: '',
      store_id: ''
    });
    setShowModal(true);
  };

  const handleEditCustomer = (customer: Customer) => {
    const cpfNumbers = (customer.cpf || '').replace(/\D/g, '');
    let formattedCpf = '';
    if (cpfNumbers.length <= 3) formattedCpf = cpfNumbers;
    else if (cpfNumbers.length <= 6) formattedCpf = `${cpfNumbers.slice(0, 3)}.${cpfNumbers.slice(3)}`;
    else if (cpfNumbers.length <= 9) formattedCpf = `${cpfNumbers.slice(0, 3)}.${cpfNumbers.slice(3, 6)}.${cpfNumbers.slice(6)}`;
    else formattedCpf = `${cpfNumbers.slice(0, 3)}.${cpfNumbers.slice(3, 6)}.${cpfNumbers.slice(6, 9)}-${cpfNumbers.slice(9, 11)}`;

    const phoneNumbers = (customer.phone || '').replace(/\D/g, '');
    let formattedPhone = '';
    if (phoneNumbers.length <= 2) formattedPhone = phoneNumbers;
    else if (phoneNumbers.length <= 7) formattedPhone = `(${phoneNumbers.slice(0, 2)}) ${phoneNumbers.slice(2)}`;
    else formattedPhone = `(${phoneNumbers.slice(0, 2)}) ${phoneNumbers.slice(2, 7)}-${phoneNumbers.slice(7, 11)}`;

    setEditingCustomer(customer);
    setFormData({
      name: customer.name,
      cpf: formattedCpf,
      email: customer.email || '',
      phone: formattedPhone,
      birth_date: customer.birth_date ? customer.birth_date.split('T')[0] : '',
      gender: customer.gender || '',
      store_id: customer.store_id.toString()
    });
    setShowModal(true);
  };

  const openCustomerDetails = async (customer: Customer) => {
    setDetailsCustomer(customer);
    setShowDetailsModal(true);
    setDetailsTab('overview');
    setLoadingHistory(true);

    try {
      const token = localStorage.getItem('auth_token');
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const [pointsResponse, redemptionsResponse] = await Promise.all([
        fetch(`${apiUrl}/customers/${customer.id}/points-history`, {
          headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' },
        }),
        fetch(`${apiUrl}/customers/${customer.id}/redemptions`, {
          headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' },
        }),
      ]);

      const pointsData = await pointsResponse.json();
      const redemptionsData = await redemptionsResponse.json();

      setPointsHistory(pointsData.success ? pointsData.data || [] : []);
      setRedemptionsHistory(redemptionsData.success ? redemptionsData.data || [] : []);
    } catch (error) {
      console.error('Erro ao carregar historico:', error);
      setPointsHistory([]);
      setRedemptionsHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleDeleteCustomer = async (customer: Customer) => {
    const result = await Swal.fire({
      title: 'Confirmar exclusão',
      text: `Tem certeza que deseja excluir o cliente ${customer.name}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sim, excluir',
      cancelButtonText: 'Cancelar',
      reverseButtons: true
    });

    if (result.isConfirmed) {
      try {
        const token = localStorage.getItem('auth_token');
        if (!token) {
          showError('Token de autenticação não encontrado. Faça login novamente.');
          navigate('/app/login');
          return;
        }

        const response = await fetch(`/api/customers/${customer.id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        if (data.success) {
          showSuccess(`Cliente ${customer.name} excluído com sucesso!`);
          loadCustomers();
        } else {
          throw new Error(data.message || 'Erro ao excluir cliente');
        }
      } catch (error: any) {
        console.error('Erro ao excluir cliente:', error);
        showError('Erro ao excluir cliente. Tente novamente.');
      }
    }
  };

  const validateForm = () => {
    // Validar CPF - agora aceita formato com máscara
    const cpfNumbers = formData.cpf.replace(/\D/g, '');
    if (cpfNumbers.length !== 11) {
      showError('CPF deve ter 11 dígitos');
      return false;
    }

    // Validar telefone - agora aceita formato com máscara
    const phoneNumbers = formData.phone.replace(/\D/g, '');
    if (phoneNumbers.length < 10 || phoneNumbers.length > 11) {
      showError('Telefone deve ter 10 ou 11 dígitos');
      return false;
    }

    // Validar email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      showError('Email inválido');
      return false;
    }

    // Validar data de nascimento
    const birthDate = new Date(formData.birth_date);
    const today = new Date();
    const age = today.getFullYear() - birthDate.getFullYear();

    if (age < 0 || age > 120) {
      showError('Data de nascimento inválida');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const sanitizedCpf = formData.cpf.replace(/\D/g, '');
      const sanitizedPhone = formData.phone.replace(/\D/g, '');
      const normalizedBirthDate = formData.birth_date
        ? new Date(formData.birth_date).toISOString().split('T')[0]
        : '';
      const storeId = formData.store_id ? Number(formData.store_id) : undefined;

      if (user && ['admin', 'super_admin'].includes(user.role || '') && !storeId) {
        showError('Selecione uma loja para o cliente.');
        setIsSubmitting(false);
        return;
      }

      const dataToSend = {
        name: formData.name.trim(),
        cpf: sanitizedCpf,
        email: formData.email.trim(),
        phone: sanitizedPhone,
        birth_date: normalizedBirthDate,
        gender: formData.gender || undefined,
        ...(storeId ? { store_id: storeId } : {})
      };

      const response = editingCustomer
        ? await apiService.updateCustomer(editingCustomer.id, dataToSend)
        : await apiService.createCustomer(dataToSend);

      if (response.success) {
        setShowModal(false);
        const action = editingCustomer ? 'atualizado' : 'cadastrado';
        const extraInfo = !editingCustomer
          ? ' Oriente o cliente a acessar com o CPF para consultar os pontos.'
          : '';
        showSuccess(`Cliente ${formData.name} ${action} com sucesso!${extraInfo}`);
        loadCustomers();
        // Limpar formulário
        setFormData({
          name: '',
          cpf: '',
          email: '',
          phone: '',
          birth_date: '',
          gender: '',
          store_id: ''
        });
      } else {
        // Tratar erros de validação (422) e outros erros do servidor
        if (response.errors) {
          // Verificar erros específicos de validação
          if (response.errors.cpf && response.errors.cpf[0]) {
            showError(response.errors.cpf[0]);
          } else if (response.errors.email && response.errors.email[0]) {
            showError(response.errors.email[0]);
          } else if (response.errors.phone && response.errors.phone[0]) {
            showError(response.errors.phone[0]);
          } else if (response.errors.store_id && response.errors.store_id[0]) {
            showError(response.errors.store_id[0]);
          } else {
            // Se há outros erros de validação, mostrar o primeiro
            const firstError = Object.values(response.errors)[0];
            if (Array.isArray(firstError) && firstError[0]) {
              showError(firstError[0] as string);
            } else {
              showError('Dados inválidos. Verifique os campos.');
            }
          }
        } else {
          // Tratar erros gerais do servidor
          showError(response.message || 'Erro ao salvar cliente');
        }
      }
    } catch (error: any) {
      console.error('Erro ao salvar cliente:', error);
      const backendMessage = error?.response?.data?.message;
      showError(backendMessage || 'Erro ao salvar cliente. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatCPF = (cpf?: string) => {
    if (!cpf) return '-';
    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const formatPhone = (phone?: string) => {
    if (!phone) return '-';
    return phone.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  };

  const formatGender = (gender?: string) => {
    if (gender === 'male') return 'Masculino';
    if (gender === 'female') return 'Feminino';
    if (gender === 'other') return 'Outro';
    return '-';
  };

  const filteredCustomers = customers.filter(customer =>
    customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (customer.cpf && customer.cpf.includes(searchTerm)) ||
    (customer.email && customer.email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const totalCustomers = customers.length;
  const totalPoints = customers.reduce((sum, customer) => sum + customer.total_points, 0);
  const avgPoints = totalCustomers > 0 ? Math.round(totalPoints / totalCustomers) : 0;

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/dashboard')}>
            <ArrowLeft size={16} />
            Voltar
          </BackButton>
          <Title>Gerenciar Clientes</Title>
        </HeaderLeft>
        <Actions>
          <SearchContainer>
            <SearchIcon size={16} />
            <SearchInput
              type="text"
              placeholder="Buscar clientes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchContainer>
          <AddButton onClick={handleAddCustomer}>
            <Plus size={16} />
            Novo Cliente
          </AddButton>
        </Actions>
      </Header>

      <StatsRow>
        <StatCard>
          <StatIcon>
            <Users size={24} />
          </StatIcon>
          <StatValue>{totalCustomers}</StatValue>
          <StatLabel>Total de Clientes</StatLabel>
        </StatCard>
        <StatCard>
          <StatIcon>
            <Star size={24} />
          </StatIcon>
          <StatValue>{totalPoints.toLocaleString()}</StatValue>
          <StatLabel>Total de Pontos</StatLabel>
        </StatCard>
        <StatCard>
          <StatIcon>
            <Gift size={24} />
          </StatIcon>
          <StatValue>{avgPoints}</StatValue>
          <StatLabel>Média de Pontos</StatLabel>
        </StatCard>
      </StatsRow>

      <TableContainer>
        {loading ? (
          <LoadingContainer>
            Carregando clientes...
          </LoadingContainer>
        ) : filteredCustomers.length === 0 ? (
          <EmptyState>
            {searchTerm ? 'Nenhum cliente encontrado para a busca.' : 'Nenhum cliente cadastrado ainda.'}
          </EmptyState>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Cliente</TableHeaderCell>
                <TableHeaderCell>CPF</TableHeaderCell>
                <TableHeaderCell>Sexo</TableHeaderCell>
                <TableHeaderCell>Contato</TableHeaderCell>
                <TableHeaderCell>Pontos</TableHeaderCell>
                <TableHeaderCell>Cadastro</TableHeaderCell>
                <TableHeaderCell>Ações</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <tbody>
              {filteredCustomers.map((customer) => (
                <TableRow key={customer.id}>
                  <TableCell>
                    <CustomerInfo>
                      <CustomerAvatar>
                        {customer.name.charAt(0).toUpperCase()}
                      </CustomerAvatar>
                      <CustomerDetails>
                        <CustomerName>{customer.name}</CustomerName>
                        <CustomerMeta>
                          <Calendar size={12} style={{ marginRight: '4px' }} />
                          {customer.birth_date ? new Date(customer.birth_date).toLocaleDateString('pt-BR') : '-'}
                        </CustomerMeta>
                      </CustomerDetails>
                    </CustomerInfo>
                  </TableCell>
                  <TableCell>{customer.cpf ? formatCPF(customer.cpf) : '-'}</TableCell>
                  <TableCell>{formatGender(customer.gender)}</TableCell>
                  <TableCell>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '2px' }}>
                        <Phone size={12} />
                        {formatPhone(customer.phone)}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#6c757d' }}>
                        <Mail size={12} />
                        {customer.email}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <PointsBadge>
                      <Star size={12} />
                      {customer.total_points}
                    </PointsBadge>
                  </TableCell>
                  <TableCell>
                    {new Date(customer.created_at).toLocaleDateString('pt-BR')}
                  </TableCell>
                  <TableCell>
                    <ActionButtons>
                      <ActionButton onClick={() => openCustomerDetails(customer)}>
                        <Eye size={14} />
                      </ActionButton>
                      <ActionButton onClick={() => handleEditCustomer(customer)}>
                        <Edit size={14} />
                      </ActionButton>
                      <ActionButton 
                        variant="delete" 
                        onClick={() => handleDeleteCustomer(customer)}
                      >
                        <Trash2 size={14} />
                      </ActionButton>
                    </ActionButtons>
                  </TableCell>
                </TableRow>
              ))}
            </tbody>
          </Table>
        )}
      </TableContainer>

      <Modal $show={showModal}>
        <ModalContent>
          <ModalTitle>
            {editingCustomer ? 'Editar Cliente' : 'Novo Cliente'}
          </ModalTitle>
          <form onSubmit={handleSubmit}>
            <FormGroup>
              <Label>Nome</Label>
              <Input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>CPF</Label>
              <Input
                type="text"
                value={formData.cpf}
                onChange={(e) => {
                  const numbers = e.target.value.replace(/\D/g, '');
                  let formatted = '';
                  if (numbers.length <= 3) formatted = numbers;
                  else if (numbers.length <= 6) formatted = `${numbers.slice(0, 3)}.${numbers.slice(3)}`;
                  else if (numbers.length <= 9) formatted = `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6)}`;
                  else formatted = `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6, 9)}-${numbers.slice(9, 11)}`;
                  setFormData({ ...formData, cpf: formatted });
                }}
                placeholder="000.000.000-00"
                maxLength={14}
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>E-mail</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>Telefone</Label>
              <Input
                type="text"
                value={formData.phone}
                onChange={(e) => {
                  const numbers = e.target.value.replace(/\D/g, '');
                  let formatted = '';
                  if (numbers.length <= 2) formatted = numbers;
                  else if (numbers.length <= 7) formatted = `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
                  else formatted = `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
                  setFormData({ ...formData, phone: formatted });
                }}
                placeholder="(00) 00000-0000"
                maxLength={15}
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>Data de Nascimento</Label>
              <Input
                type="date"
                value={formData.birth_date}
                onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })}
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>Sexo</Label>
              <select
                value={formData.gender}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    gender: e.target.value as '' | 'male' | 'female' | 'other'
                  })
                }
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e9ecef',
                  borderRadius: '8px',
                  fontSize: '14px',
                  backgroundColor: 'white'
                }}
              >
                <option value="">Selecione</option>
                <option value="male">Masculino</option>
                <option value="female">Feminino</option>
              </select>
            </FormGroup>
            {user && user.role && ['admin', 'super_admin'].includes(user.role) && (
              <FormGroup>
                <Label>Loja *</Label>
                <select
                  value={formData.store_id}
                  onChange={(e) => setFormData({ ...formData, store_id: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e9ecef',
                    borderRadius: '8px',
                    fontSize: '14px',
                    backgroundColor: 'white'
                  }}
                >
                  <option value="">Selecione uma loja</option>
                  {stores.map(store => (
                    <option key={store.id} value={store.id}>
                      {store.name}
                    </option>
                  ))}
                </select>
              </FormGroup>
            )}
            <ModalActions>
              <ModalButton type="button" onClick={() => setShowModal(false)}>
                Cancelar
              </ModalButton>
              <ModalButton type="submit" variant="primary" disabled={isSubmitting}>
                {isSubmitting ? 'Salvando...' : (editingCustomer ? 'Salvar' : 'Criar')}
              </ModalButton>
            </ModalActions>
          </form>
        </ModalContent>
      </Modal>

      <Modal $show={showDetailsModal}>
        <ModalContent>
          <ModalTitle>Detalhes do Cliente</ModalTitle>
          {detailsCustomer && (
            <>
              <Tabs>
                <TabButton type="button" $active={detailsTab === 'overview'} onClick={() => setDetailsTab('overview')}>
                  Visão geral
                </TabButton>
                <TabButton type="button" $active={detailsTab === 'points'} onClick={() => setDetailsTab('points')}>
                  Pontos
                </TabButton>
                <TabButton type="button" $active={detailsTab === 'redemptions'} onClick={() => setDetailsTab('redemptions')}>
                  Resgates
                </TabButton>
              </Tabs>

              {detailsTab === 'overview' && (
                <DetailGrid>
                  <DetailCard>
                    <strong>Nome</strong>
                    <div>{detailsCustomer.name}</div>
                  </DetailCard>
                  <DetailCard>
                    <strong>Sexo</strong>
                    <div>{formatGender(detailsCustomer.gender)}</div>
                  </DetailCard>
                  <DetailCard>
                    <strong>CPF</strong>
                    <div>{detailsCustomer.cpf ? formatCPF(detailsCustomer.cpf) : '-'}</div>
                  </DetailCard>
                  <DetailCard>
                    <strong>Email</strong>
                    <div>{detailsCustomer.email || '-'}</div>
                  </DetailCard>
                  <DetailCard>
                    <strong>Telefone</strong>
                    <div>{formatPhone(detailsCustomer.phone)}</div>
                  </DetailCard>
                  <DetailCard>
                    <strong>Total de pontos</strong>
                    <div>{detailsCustomer.total_points}</div>
                  </DetailCard>
                </DetailGrid>
              )}

              {detailsTab === 'points' && (
                <>
                  {loadingHistory ? (
                    <LoadingContainer>Carregando histórico...</LoadingContainer>
                  ) : (
                    <HistoryList>
                      {pointsHistory.length === 0 ? (
                        <EmptyState>Sem pontos registrados.</EmptyState>
                      ) : (
                        pointsHistory.map((item) => (
                          <HistoryItem key={item.id}>
                            <div>
                              <strong>{item.type === 'earned' ? 'Pontos ganhos' : 'Pontos resgatados'}</strong>
                              <div>{item.description || 'Sem descrição'}</div>
                              <HistoryMeta>{new Date(item.created_at).toLocaleString('pt-BR')}</HistoryMeta>
                            </div>
                            <div>
                              <strong>{item.points}</strong>
                            </div>
                          </HistoryItem>
                        ))
                      )}
                    </HistoryList>
                  )}
                </>
              )}

              {detailsTab === 'redemptions' && (
                <>
                  {loadingHistory ? (
                    <LoadingContainer>Carregando resgates...</LoadingContainer>
                  ) : (
                    <HistoryList>
                      {redemptionsHistory.length === 0 ? (
                        <EmptyState>Sem resgates registrados.</EmptyState>
                      ) : (
                        redemptionsHistory.map((item) => (
                          <HistoryItem key={item.id}>
                            <div>
                              <strong>{item.reward?.name || 'Recompensa'}</strong>
                              <div>Status: {item.status || '—'}</div>
                              <HistoryMeta>{item.issued_at ? new Date(item.issued_at).toLocaleString('pt-BR') : '-'}</HistoryMeta>
                            </div>
                            <div>
                              <strong>{item.reward?.points_required || '-'}</strong>
                            </div>
                          </HistoryItem>
                        ))
                      )}
                    </HistoryList>
                  )}
                </>
              )}
            </>
          )}
          <ModalActions>
            <ModalButton type="button" onClick={() => setShowDetailsModal(false)}>
              Fechar
            </ModalButton>
          </ModalActions>
        </ModalContent>
      </Modal>
    </Container>
  );
};
